import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";

import { type HeaderCellProps } from "./HeaderCell.js";

export interface CheckboxHeaderCellProps extends HeaderCellProps {
  onChange: (selected: boolean) => void;
  defaultChecked: boolean;
  uniqueGridIdentifier: string;
  setCheckboxRef: (component: CheckboxHeaderCellRef) => void;
}

export interface CheckboxHeaderCellRef {
  setSelected: (selected: boolean) => void;
}

export const CheckboxHeaderCell = forwardRef<CheckboxHeaderCellRef, CheckboxHeaderCellProps>((props, ref) => {
  const { setCheckboxRef, onChange } = props;
  const [checked, setChecked] = useState<boolean>(props.defaultChecked);

  const setSelected = useCallback(
    (selected: boolean) => {
      if (checked !== selected) {
        setChecked(selected);
      }
    },
    [checked],
  );

  useImperativeHandle(
    ref,
    () => ({
      setSelected,
    }),
    [setSelected],
  );

  useEffect(() => {
    const refObject = { setSelected };
    setCheckboxRef(refObject);
  }, [setCheckboxRef, setSelected]);

  const handleClick = useCallback(() => {
    const newCheckedState = !checked;
    setSelected(newCheckedState);
    onChange(newCheckedState);
  }, [checked, onChange, setSelected]);

  const checkboxId = `headerCheckbox_${props.uniqueGridIdentifier}`;

  return (
    <div className="checkbox" style={{ paddingLeft: 5 }} data-testid={"grid-select-all"}>
      <input type="checkbox" id={checkboxId} checked={checked} onChange={handleClick} data-checked={checked} />
      <label htmlFor={checkboxId} />
    </div>
  );
});

CheckboxHeaderCell.displayName = "CheckboxHeaderCell";
